import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Globe, MapPin, Phone, Settings, Share2 } from "lucide-react";

type BusinessProfileHeroProps = {
  name: string;
  englishName?: string | null;
  description?: string | null;
  coverImageUrl?: string | null;
  categoryLabel: string;
  foodTags?: string[];
  verified: boolean;
  googleLinked: boolean;
  membershipTier?: string | null;
  phone?: string | null;
  website?: string | null;
  isOwner: boolean;
  onPhone: () => void;
  onDirections: () => void;
  onShare: () => void;
  onManage: () => void;
};

export function BusinessProfileHero({
  name, englishName, description, coverImageUrl, categoryLabel, foodTags = [],
  verified, googleLinked, membershipTier, phone, website, isOwner,
  onPhone, onDirections, onShare, onManage,
}: BusinessProfileHeroProps) {
  return (
    <section className="mb-6 overflow-hidden rounded-2xl border bg-card shadow-sm">
      <div className={`grid ${coverImageUrl ? "lg:grid-cols-[1.15fr_0.85fr]" : ""}`}>
        <div className="flex min-h-[260px] flex-col justify-between gap-8 bg-gradient-to-br from-primary/10 via-background to-background p-6 sm:p-8 lg:p-10">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-background/80">{categoryLabel}</Badge>
              {foodTags.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}
              {verified && <Badge className="gap-1 bg-emerald-600"><CheckCircle2 className="h-3 w-3" />사업자 인증</Badge>}
              {googleLinked && <Badge variant="secondary" className="gap-1"><MapPin className="h-3 w-3" />Google 장소 확인</Badge>}
              {membershipTier && membershipTier !== "free" && <Badge>프리미엄</Badge>}
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{name}</h1>
              {englishName && englishName !== name && <p className="mt-2 text-lg text-muted-foreground">{englishName}</p>}
            </div>
            {description && <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">{description}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {phone && <Button onClick={onPhone}><Phone className="mr-2 h-4 w-4" />전화하기</Button>}
            <Button variant={phone ? "outline" : "default"} onClick={onDirections}><MapPin className="mr-2 h-4 w-4" />길찾기</Button>
            {website && <Button asChild variant="outline"><a href={website} target="_blank" rel="noopener noreferrer"><Globe className="mr-2 h-4 w-4" />홈페이지</a></Button>}
            <Button variant="ghost" size="icon" onClick={onShare} aria-label="공유"><Share2 className="h-4 w-4" /></Button>
            {isOwner && <Button className="sm:ml-auto" variant="secondary" onClick={onManage}><Settings className="mr-2 h-4 w-4" />내 홈페이지 관리</Button>}
          </div>
        </div>
        {coverImageUrl && <img src={coverImageUrl} alt={`${name} 대표 이미지`} className="h-64 w-full object-cover lg:h-full lg:min-h-[320px]" />}
      </div>
    </section>
  );
}
